/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import { QCSearchPage } from './QCSearchPage';
import { PipelinePage, Sidebar as PipelineSidebar } from './PipelinePage';
import { DashboardPage, Sidebar as DashboardSidebar } from './DashboardPage';
import { Sidebar as ProfileSidebar } from './ProfilePage/Sidebar';
import { ExtendedRegistrationPage } from './ExtendedRegistrationPage';
import { ProfilePage } from './ProfilePage';
import { SelectTenantPage } from './SelectTenantPage';
import { Sidebar as StatsDashboardSidebar, StatsPage } from './StatsDashboardPage';
import { RoutePermissions } from 'blb-utils/permissions';
import { PERMISSION_NAMES } from 'blb-types/blendBridge';
import { NotaryPipelinePage } from 'blb-components/AuthenticatedApp/RON/NotaryPipeline';
import { NotaryPage } from 'blb-components/AuthenticatedApp/RON/NotaryPage/NotaryPage';
import { NotarySidebar } from 'blb-components/AuthenticatedApp/RON/SideBar/NotarySidebar';
import { SecondarySidebar } from 'blb-components/AuthenticatedApp/RON/SideBar/SecondarySidebar';
import { TeamsPage } from 'blb-components/AuthenticatedApp/TeamsPage';
import { DocumentViewerPage } from 'blb-components/AuthenticatedApp/DocumentViewer/DocumentViewerPage';

export type Route =
  | '/:loanId/dashboard'
  | '/notaries'
  | '/notary/:principalId/dashboard'
  | '/pipeline'
  | '/profile'
  | '/qc-search'
  | '/registration'
  | '/stats'
  | '/teams'
  | '/select-tenant'
  | '/:loanId/sign-ahead-documents'
  | '/:loanId/enote';

export interface RouteConfiguration {
  MainPaneContent: React.FC;
  SideBarContent: React.FC | null;
  path: Route;
  permissions?: RoutePermissions;
}

export const allRoutesConfigurations: RouteConfiguration[] = [
  {
    MainPaneContent: PipelinePage,
    path: '/pipeline',
    permissions: {
      [PERMISSION_NAMES.BridgePipeline]: ['notary'],
    },
    SideBarContent: PipelineSidebar,
  },
  {
    MainPaneContent: QCSearchPage,
    path: '/qc-search',
    SideBarContent: PipelineSidebar,
  },
  {
    MainPaneContent: DashboardPage,
    path: '/:loanId/dashboard',
    permissions: {
      [PERMISSION_NAMES.BridgePipeline]: ['notary'],
    },
    SideBarContent: DashboardSidebar,
  },
  {
    MainPaneContent: ProfilePage,
    path: '/profile',
    SideBarContent: ProfileSidebar,
    // TODO: add RoutePermissions here when we have a 404 page
  },
  {
    MainPaneContent: StatsPage,
    path: '/stats',
    SideBarContent: StatsDashboardSidebar,
  },
  {
    MainPaneContent: NotaryPipelinePage,
    path: '/notaries',
    permissions: {
      [PERMISSION_NAMES.BSRAdmin]: ['notary'],
    },
    SideBarContent: NotarySidebar,
  },
  {
    MainPaneContent: NotaryPage,
    path: '/notary/:principalId/dashboard',
    permissions: {
      [PERMISSION_NAMES.BSRAdmin]: ['notary'],
    },
    SideBarContent: SecondarySidebar,
  },
  {
    MainPaneContent: TeamsPage,
    path: '/teams',
    SideBarContent: ProfileSidebar,
  },
  {
    MainPaneContent: ExtendedRegistrationPage,
    path: '/registration',
    SideBarContent: null,
  },
  {
    MainPaneContent: SelectTenantPage,
    path: '/select-tenant',
    SideBarContent: null,
  },
  {
    MainPaneContent: DocumentViewerPage,
    path: '/:loanId/sign-ahead-documents',
    SideBarContent: null,
  },
  {
    MainPaneContent: DocumentViewerPage,
    path: '/:loanId/enote',
    SideBarContent: null,
  },
];

export type MandatoryPostLoginRoute = '/registration';

export const mandatoryPostLoginRoutesMap: Record<MandatoryPostLoginRoute, RouteConfiguration[]> = {
  '/registration': [
    {
      MainPaneContent: ExtendedRegistrationPage,
      path: '/registration',
      SideBarContent: null,
    },
  ],
};

export const getRouteConfigurations = (routes: Route[]) =>
  allRoutesConfigurations.filter(routeConfig => routes.includes(routeConfig.path));
